import type { ComponentPropsWithoutRef } from "react";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { Spinner } from "./Spiner";

type ButtonProps = ComponentPropsWithoutRef<"button"> & {
  isLoading?: boolean;
};

export const ButtonSecondary = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { children, className, isLoading, ...rest } = props;
  return (
    <button
      ref={ref}
      className={twMerge("inline-flex min-h-[48px] max-w-xs items-center justify-center bg-black/25 px-4 py-1 text-primary", className)}
      {...rest}
    >
      {isLoading ? <Spinner /> : children}
    </button>
  );
});

export const ButtonSecondarySm = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { children, className, isLoading, ...rest } = props;
  return (
    <button
      ref={ref}
      className={twMerge("inline-flex min-h-[36px] max-w-xs items-center justify-center bg-black/25 px-2 py-1 text-sm", className)}
      {...rest}
    >
      {isLoading ? <Spinner /> : children}
    </button>
  );
});

export const ButtonPrimary = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { children, className, isLoading, ...rest } = props;
  return (
    <button
      ref={ref}
      className={twMerge(
        "inline-flex max-w-xs justify-center bg-[linear-gradient(65deg,_#D69B2D_-4%,_#FFD27E_118%)] px-4 py-3 text-base font-bold text-secondary-100 focus:bg-primary focus:brightness-75 hover:!bg-[linear-gradient(0deg,_#D69B2D,_#D69B2D)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:!bg-[linear-gradient(65deg,_#D69B2D_-4%,_#FFD27E_118%)]",
        className
      )}
      {...rest}
    >
      {isLoading ? <Spinner /> : children}
    </button>
  );
});

export const ButtonLink = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { children, className, isLoading, ...rest } = props;
  return (
    <button
      ref={ref}
      className={twMerge(
        "inline-flex min-h-[48px] max-w-xs items-center justify-center px-4 py-1 text-primary disabled:text-primary/50",
        className
      )}
      {...rest}
    >
      {isLoading ? <Spinner /> : children}
    </button>
  );
});

export const ButtonPrimarySm = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { children, className, isLoading, ...rest } = props;
  return (
    <button
      ref={ref}
      className={twMerge(
        "inline-flex min-h-[36px] max-w-xs items-center justify-center bg-[linear-gradient(65deg,_#D69B2D_-4%,_#FFD27E_118%)] px-4 py-1 text-sm font-bold text-dark focus:bg-primary focus:brightness-75 hover:!bg-[linear-gradient(0deg,_#D69B2D,_#D69B2D)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:!bg-[linear-gradient(65deg,_#D69B2D_-4%,_#FFD27E_118%)]",
        className
      )}
      {...rest}
    >
      {isLoading ? <Spinner /> : children}
    </button>
  );
});

export const ButtonInfo = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { children, className, isLoading, ...rest } = props;
  return (
    <button
      ref={ref}
      className={twMerge(
        "inline-flex min-h-[48px] max-w-xs items-center justify-center bg-info-100 px-4 py-2 text-sm font-medium text-secondary-6",
        className
      )}
      {...rest}
    >
      {isLoading ? <Spinner /> : children}
    </button>
  );
});
